import { Activity } from 'src/services/activities/activities.types';
import { JobApplication } from 'src/services/applications/applications.types';
import { IntegrationResource } from 'src/services/integration/integrations.types';

import { rootApi } from '../rootApi';
import { OrganisationDocument } from '../files/file.types';
import { APIResponse, PageOptions, HTTPRequestType, PaginatedResponse } from '../api.types';
import {
  IJob,
  FeeType,
  JobInvite,
  IJobBoard,
  JobSummary,
  CreateJobDTO,
  JobsResponse,
  LinkedAgency,
  IJobQuestion,
  ImportJobProps,
  JobLinkRequest,
  RequestJobLink,
  IMinimalJobBoard,
  JobChecklistItem,
  JobImportResponse,
  SendJobInviteProps,
  GetJobsRequestProps,
  JobChecklistResponse,
  IApplyResultResponse,
  IApplyIntentResponse,
  JobBoardAvailability,
  AddToJobRequestProps,
  GetLinkedAgenciesProps,
  JobStakeholderResponse,
  UpdateRoleRequestProps,
  IConnectResultResponse,
  IConnectIntentResponse,
  GetJobLinkRequestsProps,
  GetJobBoardRequestProps,
  InitiateApplyFlowRequest,
  JobLinkActionRequestProps,
  JobInviteActionRequestProps,
  UpdateApplicationStatusProps,
  InitiateConnectIntentRequest,
  ShareJobDocumentRequestProps,
  AssignStakeholderRequestProps,
  RemoveStakeholderRequestProps,
  UpdateStakeholderRequestProps,
  UpdateJobBoardAvailabilityDto,
  GetJobStakeholdersRequestProps,
} from './jobs.types';

export const jobsApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({

    getBoardByOrganization: builder.query<IJobBoard, { organizationId: string }>({
      query: ({ organizationId }) => ({
        url: `/organizations/${organizationId}/job-board`,
      }),
    }),
    getBoardById: builder.query<IMinimalJobBoard, { boardId: string }>({
      query: ({ boardId }) => ({
        url: `/jobs/boards/${boardId}`,
      }),
    }),
    getBoardJobs: builder.query<JobsResponse, GetJobBoardRequestProps>({
      query: ({ board_id, page, per_page, search_query, employment_type, work_style, job_type, industry }) => ({
        url: `/jobs/boards/${board_id}/jobs`,
        params: {
          page,
          per_page,
          search_query,
          employment_type,
          work_style,
          job_type,
          industry,
        },
      })
    }),
    getJobBoardAvailabilities: builder.query<JobBoardAvailability[], { job_id: string }>({
      query: ({ job_id }) => ({
        url: `/jobs/${job_id}/boards`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'JobBoardAvailability', id: arg.job_id }],
    }),
    getBoardJobById: builder.query<IJob, { boardId: string, jobId: string }>({
      query: ({ boardId, jobId }) => ({
        url: `/jobs/boards/${boardId}/jobs/${jobId}`,
      }),
    }),
    initiateApplyFlow: builder.mutation<IApplyResultResponse, InitiateApplyFlowRequest>({
      query: (data) => ({
        url: `/jobs/boards/${data.board_id}/jobs/${data.job_id}/apply`,
        method: HTTPRequestType.POST,
        data: {
          flow_type: data.flow_type,
          form_data: data.form_data,
          account_data: data.account_data,
          compliance_submissions: data.compliance_submissions,
          source: data.source
        }
      }),
    }),
    getApplyIntent: builder.query<IApplyIntentResponse, { token: string }>({
      query: ({ token }) => ({
        url: `/apply-intents?token=${token}`,
      }),
    }),

    getConnectIntent: builder.query<IConnectIntentResponse, { token: string }>({
      query: ({ token }) => ({
        url: `/connect-intents?token=${token}`,
      }),
    }),
    initiateConnectIntent: builder.mutation<IConnectResultResponse, InitiateConnectIntentRequest>({
      query: (data) => ({
        url: `/jobs/boards/${data.board_id}/connect/initiate`,
        method: HTTPRequestType.POST,
        data: {
          flow_type: data.flow_type,
          form_data: data.form_data,
          account_data: data.account_data,
          list_ids: data.list_ids,
          recaptcha_token: data.recaptcha_token
        }
      }),
    }),

    getMyActiveJobs: builder.query<JobsResponse, GetJobsRequestProps | void>({
      query: (data?: GetJobsRequestProps) => ({
        url: `/jobs/my-jobs`,
        params: data,
      }),
      providesTags: ['Job'],
    }),

    getOrganisationJobs: builder.query<JobsResponse, GetJobsRequestProps | void>({
      query: (data?: GetJobsRequestProps) => ({
        url: `/jobs`,
        params: data,
      }),
      providesTags: ['Job'],
    }),

    saveToDashboard: builder.mutation<IJob, { jobId: string }>({
      query: ({ jobId }) => ({
        url: `/jobs/${jobId}/save-to-dashboard`,
        method: HTTPRequestType.POST,
      })
    }),

    createJob: builder.mutation<IJob, CreateJobDTO>({
      query: (data) => ({
        url: '/jobs',
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: ['Job', 'Clients', 'Recruiters'],
    }),

    getJobById: builder.query<IJob, string>({
      query: (id) => ({
        url: `/jobs/${id}`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'Job', id: arg }],
    }),

    getJobSummary: builder.query<JobSummary, string>({
      query: (id) => ({
        url: `/jobs/${id}/summary`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'JobSummary', id: arg }],
    }),

    getJobActivities: builder.query<PaginatedResponse<Activity>, { jobId: string; page: number; perPage: number }>({
      query: ({ jobId, perPage, page }) => ({
        url: `/jobs/${jobId}/activities`,
        params: {
          page,
          per_page: perPage,
        },
      }),
      providesTags: (_result, _error, arg) => [{ type: 'JobActivities', id: arg.jobId }],
    }),

    updateJob: builder.mutation<IJob, Partial<CreateJobDTO>>({
      query: (data) => ({
        url: `/jobs/${data.id}`,
        data,
        method: HTTPRequestType.PUT,
      }),
      invalidatesTags: (_result, _error, arg) => [
        'Job',
        { type: 'Job', id: arg.id },
        'Clients',
        'Recruiters',
        'Nudges',
      ],
    }),

    regenerateJobBoardCode: builder.mutation<IJob, string>({
      query: (jobId) => ({
        url: `/jobs/${jobId}/regenerate-job-board-code`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: (_result, _error, jobId) => ['Job', { type: 'Job', id: jobId }],
    }),

    updateRoleData: builder.mutation<APIResponse, UpdateRoleRequestProps>({
      query: ({ jobId, data }) => ({
        url: `/jobs/${jobId}/role`,
        data,
        method: HTTPRequestType.PUT,
      }),
      invalidatesTags: ['Job', 'Nudges'],
    }),

    requestJobLink: builder.mutation<IJob, RequestJobLink>({
      query: (request) => ({
        url: `/jobs/${request.job_id}/links/requests`,
        method: HTTPRequestType.POST,
        data: request.data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Job', id: arg.job_id }],
    }),

    cancelVerificationRequests: builder.mutation<APIResponse, { job_id: string }>({
      query: (data) => ({
        url: `/jobs/${data.job_id}/links/verification-requests/cancel`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Job', id: arg.job_id }],
    }),

    getIncomingJobLinkRequests: builder.query<PaginatedResponse<JobLinkRequest>, GetJobLinkRequestsProps>({
      query: (params) => ({
        url: '/jobs/links/requests/incoming',
        params,
      }),
      providesTags: ['JobLinkRequest'],
    }),

    getOutgoingJobLinkRequests: builder.query<PaginatedResponse<JobLinkRequest>, GetJobLinkRequestsProps>({
      query: (params) => ({
        url: '/jobs/links/requests/outgoing',
        params,
      }),
      providesTags: ['JobLinkRequest'],
    }),

    getJobLinkRequestById: builder.query<JobLinkRequest, string>({
      query: (id: string) => ({
        url: `/jobs/links/requests/${id}`,
      }),
    }),

    acceptJobLinkRequest: builder.mutation<IJob, JobLinkActionRequestProps>({
      query: (request) => ({
        url: `/jobs/links/requests/${request.requestId}/accept`,
        data: request.data,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['Job', 'JobLinkRequest', 'Clients', 'Recruiters'],
    }),

    rejectJobLinkRequest: builder.mutation<
      APIResponse,
      Pick<JobLinkActionRequestProps, 'requestId'>
    >({
      query: ({ requestId }) => ({
        url: `/jobs/links/requests/${requestId}/reject`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['Job', 'JobLinkRequest', 'Clients', 'Recruiters'],
    }),

    approveJob: builder.mutation<APIResponse, string>({
      query: (jobId) => ({
        url: `/jobs/${jobId}/approve`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['Job'],
    }),

    getLinkedAgencies: builder.query<PaginatedResponse<LinkedAgency>, GetLinkedAgenciesProps>({
      query: (data) => ({
        url: `/jobs/${data.jobId}/links`,
        params: data.pageOptions,
      }),
      providesTags: (_results, _error, _arg) => [{ type: 'LinkedAgencies', id: _arg.jobId }],
    }),

    updateJobApplicationStatus: builder.mutation<APIResponse, UpdateApplicationStatusProps>({
      query: (props) => ({
        url: `/jobs/${props.jobId}/application-status`,
        data: { status: props.status },
        method: HTTPRequestType.PUT,
      }),
      invalidatesTags: ['Job'],
    }),

    // Job Invites  ------------------------------------------------

    getJobInvitesByJob: builder.query<PaginatedResponse<JobInvite>, { jobId: string; page: number; perPage: number }>({
      query: (params) => ({
        url: `/jobs/${params.jobId}/invites`,
        params: {
          page: params.page,
          per_page: params.perPage,
        },
      }),
      providesTags: (_results, _error, _arg) => [{ type: 'JobInvites', id: _arg.jobId }],
    }),

    sendJobInvite: builder.mutation<APIResponse, SendJobInviteProps>({
      query: (props) => ({
        url: `/jobs/${props.jobId}/invites`,
        data: props.data,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: (_results, _error, _arg) => [
        { type: 'JobInvites', id: _arg.jobId },
        { type: 'JobSummary', id: _arg.jobId },
        'JobInvites',
      ],
    }),

    getIncomingJobInvites: builder.query<PaginatedResponse<JobInvite>, PageOptions>({
      query: (params) => ({
        url: '/jobs/invites/incoming',
        params,
      }),
      providesTags: ['JobInvites'],
    }),

    getJobInviteById: builder.query<JobInvite, string>({
      query: (id) => ({
        url: `/jobs/invites/${id}`,
      }),
    }),

    getOutgoingJobInvites: builder.query<PaginatedResponse<JobInvite>, PageOptions & { job_id?: string }>({
      query: (params) => ({
        url: '/jobs/invites/outgoing',
        params,
      }),
      providesTags: ['JobInvites'],
    }),

    cancelJobInvite: builder.mutation<APIResponse, string>({
      query: (inviteId) => ({
        url: `/jobs/invites/${inviteId}/cancel`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['JobInvites', 'JobSummary'],
    }),

    resendJobInvite: builder.mutation<APIResponse, string>({
      query: (inviteId) => ({
        url: `/jobs/invites/${inviteId}/resend`,
        method: HTTPRequestType.POST,
      })
    }),

    actionJobInvite: builder.mutation<APIResponse, JobInviteActionRequestProps>({
      query: (request) => ({
        url: `/jobs/invites/${request.requestId}/action`,
        data: request.data,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['JobInvites', 'Job', 'Clients', 'Recruiters'],
    }),

    importJob: builder.mutation<JobImportResponse, ImportJobProps>({
      query: (request) => ({
        url: `/jobs/import`,
        data: request.data,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['Job', 'Clients', 'Recruiters'],
    }),

    archiveJob: builder.mutation<APIResponse, string>({
      query: (jobId) => ({
        url: `/jobs/${jobId}/archive`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['Job', 'Nudges'],
    }),

    updateJobBoardAvailability: builder.mutation<APIResponse, { boardId: string; dto: UpdateJobBoardAvailabilityDto }>({
      query: ({ boardId, dto }) => ({
        url: `/jobs/boards/${boardId}/availability`,
        method: HTTPRequestType.POST,
        data: dto
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'JobBoardAvailability', id: arg.dto.job_id }],
    }),

    getJobChecklist: builder.query<JobChecklistResponse, string>({
      query: (jobId) => ({
        url: `/jobs/${jobId}/checklist`,
      }),
      providesTags: ['Checklist'],
    }),

    updateJobChecklist: builder.mutation<
      APIResponse,
      { jobId: string; items: Pick<JobChecklistItem, 'id' | 'checked'>[] }
    >({
      query: ({ jobId, items }) => ({
        url: `/jobs/${jobId}/checklist/items`,
        method: HTTPRequestType.PUT,
        data: {
          items,
        },
      }),
      invalidatesTags: ['Checklist'],
    }),

    getJobDocuments: builder.query<OrganisationDocument[], string>({
      query: (jobId) => ({
        url: `/jobs/${jobId}/documents`,
      }),
      providesTags: (_results, _error, _arg) => [{ type: 'JobDocuments', id: _arg }],
    }),

    addDocumentToJob: builder.mutation<APIResponse, { jobId: string; documentId: string }>({
      query: ({ jobId, documentId }) => ({
        url: `/jobs/${jobId}/documents/add`,
        method: HTTPRequestType.POST,
        data: { document_id: documentId },
      }),
      invalidatesTags: (_results, _error, arg) => [{ type: 'JobDocuments', id: arg.jobId }]
    }),
    removeDocumentFromJob: builder.mutation<APIResponse, { jobId: string; documentId: string }>({
      query: ({ jobId, documentId }) => ({
        url: `/jobs/${jobId}/documents/remove`,
        method: HTTPRequestType.POST,
        data: { document_id: documentId },
      }),
      invalidatesTags: (_results, _error, arg) => [{ type: 'JobDocuments', id: arg.jobId }]
    }),
    shareJobDocument: builder.mutation<APIResponse, { jobId: string; data: ShareJobDocumentRequestProps }>({
      query: ({ jobId, data }) => ({
        url: `/jobs/${jobId}/documents/${data.document_id}/share`,
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (_results, _error, arg) => [{ type: 'JobDocuments', id: arg.jobId }],
    }),

    getJobStakeholders: builder.query<PaginatedResponse<JobStakeholderResponse>, GetJobStakeholdersRequestProps>({
      query: (data) => ({
        url: `/jobs/${data.job_id}/stakeholders`,
        params: {
          page: data.page,
          per_page: data.per_page,
          search_query: data.search_query,
        },
      }),
      providesTags: ['Stakeholders'],
    }),

    addStakeholderToJob: builder.mutation<APIResponse, AssignStakeholderRequestProps>({
      query: ({ job_id, ...data }) => ({
        url: `/jobs/${job_id}/stakeholders/add`,
        method: HTTPRequestType.POST,
        data: {
          account_id: data.account_id,
          labels: data.labels,
          visibility: data.visibility,
        },
      }),
      invalidatesTags: ['Stakeholders'],
    }),

    updateJobStakeholder: builder.mutation<APIResponse, UpdateStakeholderRequestProps>({
      query: ({ job_id, account_id, ...data }) => ({
        url: `/jobs/${job_id}/stakeholders/${account_id}`,
        method: HTTPRequestType.PUT,
        data: {
          labels: data.labels,
          visibility: data.visibility,
        },
      }),
      invalidatesTags: ['Stakeholders'],
    }),

    removeStakeholderFromJob: builder.mutation<APIResponse, RemoveStakeholderRequestProps>({
      query: ({ job_id, ...data }) => ({
        url: `/jobs/${job_id}/stakeholders/remove`,
        method: HTTPRequestType.POST,
        data: {
          account_id: data.account_id,
        },
      }),
      invalidatesTags: ['Stakeholders'],
    }),

    // integrations
    getJobIntegrations: builder.query<IntegrationResource[], { job_id: string }>({
      query: ({ job_id }) => ({
        url: `/jobs/${job_id}/integrations`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'JobIntegrations', id: arg.job_id }],
    }),

    requestJobChanges: builder.mutation<APIResponse, { job_id: string; notes: string }>({
      query: ({ job_id, notes }) => ({
        url: `/jobs/${job_id}/request-job-update`,
        method: HTTPRequestType.POST,
        data: { notes },
      }),
      invalidatesTags: ['Job'],
    }),

    rejectJobChanges: builder.mutation<APIResponse, { job_id: string }>({
      query: ({ job_id }) => ({
        url: `/jobs/${job_id}/reject-job-update`,
        method: HTTPRequestType.POST,
      }),
      invalidatesTags: ['Job'],
    }),

    // fee settings
    updateLinkSettings: builder.mutation<
      APIResponse,
      { job_id: string; organization_id: string; fee_type: FeeType; fee_value: number; shortlist_candidate_limit: number | null }
    >({
      query: ({ job_id, organization_id, fee_type, fee_value, shortlist_candidate_limit }) => ({
        url: `/jobs/${job_id}/links/${organization_id}/settings`,
        method: HTTPRequestType.PUT,
        data: { fee_type, fee_value, shortlist_candidate_limit },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'LinkedAgencies', id: arg.job_id }],
    }),

    // compliance
    getComplianceQuestionsByJob: builder.query<IJobQuestion[], { jobId: string }>({
      query: ({ jobId }) => ({
        url: `/jobs/${jobId}/compliance/questions`,
      }),
    }),

    // candidate manager
    addCandidateToJob: builder.mutation<JobApplication, AddToJobRequestProps>({
      query: (data) => ({
        url: `/jobs/${data.job_id}/candidates/add`,
        method: HTTPRequestType.POST,
        data: {
          candidate_id: data.candidate_id,
          create_invite: data.create_invite,
          default_stage: data.default_stage,
          document_ids: data.document_ids
        }
      })
    }),

    inviteApplicationCandidate: builder.mutation<APIResponse, { application_id: string; }>({
      query: (data) => ({
        url: `/jobs/applications/${data.application_id}/invite-candidate`,
        method: HTTPRequestType.POST
      })
    }),

  }),
});

export const {
  useGetMyActiveJobsQuery,
  useGetOrganisationJobsQuery,

  // Job Boards
  useGetBoardByOrganizationQuery,
  useGetBoardByIdQuery,
  useGetBoardJobsQuery,
  useGetBoardJobByIdQuery,
  useInitiateApplyFlowMutation,
  useGetApplyIntentQuery,
  useGetConnectIntentQuery,
  useInitiateConnectIntentMutation,
  useGetJobBoardAvailabilitiesQuery,

  useCreateJobMutation,
  useGetJobByIdQuery,
  useGetJobSummaryQuery,
  useGetJobActivitiesQuery,
  useUpdateJobMutation,
  useRegenerateJobBoardCodeMutation,
  useRequestJobLinkMutation,
  useCancelVerificationRequestsMutation,

  // Job Links
  useGetIncomingJobLinkRequestsQuery,
  useGetOutgoingJobLinkRequestsQuery,
  useGetJobLinkRequestByIdQuery,
  useAcceptJobLinkRequestMutation,
  useRejectJobLinkRequestMutation,

  // Job Actions
  useSaveToDashboardMutation,
  useApproveJobMutation,
  useGetLinkedAgenciesQuery,
  useUpdateJobApplicationStatusMutation,
  useGetJobInvitesByJobQuery,
  useSendJobInviteMutation,
  useGetIncomingJobInvitesQuery,
  useActionJobInviteMutation,
  useGetOutgoingJobInvitesQuery,
  useCancelJobInviteMutation,
  useResendJobInviteMutation,
  useImportJobMutation,
  useArchiveJobMutation,
  useUpdateJobBoardAvailabilityMutation,
  useGetJobChecklistQuery,
  useUpdateJobChecklistMutation,

  useGetJobDocumentsQuery,
  useAddDocumentToJobMutation,
  useRemoveDocumentFromJobMutation,
  useShareJobDocumentMutation,

  useLazyGetJobStakeholdersQuery,
  useGetJobStakeholdersQuery,
  useAddStakeholderToJobMutation,
  useUpdateJobStakeholderMutation,
  useRemoveStakeholderFromJobMutation,
  useLazyGetJobInviteByIdQuery,
  useGetJobInviteByIdQuery,
  useUpdateRoleDataMutation,

  useGetJobIntegrationsQuery,

  // Job Changes
  useRequestJobChangesMutation,
  useRejectJobChangesMutation,

  // Fee Settings
  useUpdateLinkSettingsMutation,

  // Compliance
  useGetComplianceQuestionsByJobQuery,

  // Candidate Manager
  useAddCandidateToJobMutation,
  useInviteApplicationCandidateMutation,
} = jobsApi;
